import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import { setConfig } from "@/services/api/config";
import type { APIError } from "@/services/api/apiRequest";
import { CLERK_PUBLISHABLE_KEY, clerkEnabled } from "@/services/clerk";
import App from "@/App";
// The whole design system: Tailwind 4 + shadcn tokens, the nudista heading
// face, and Tailwind's preflight in place of the template's reset.
import "@/styles/globals.css";

// Wire the API client to the build-time env.
setConfig({
  apiUrl: import.meta.env.VITE_API_URL,
  env: import.meta.env.MODE,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx is an answer, not a network blip. React Query's default is three
      // retries with backoff, which meant a page the server had already
      // refused spun its loading skeleton for four seconds before it could
      // say "members only". Anything else still gets a second go.
      retry: (failureCount, error) => {
        const status = (error as APIError).status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const app = (
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {clerkEnabled ? (
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY!}
        signInUrl="/sign-in"
      >
        {app}
      </ClerkProvider>
    ) : (
      app
    )}
  </React.StrictMode>
);
